import classNames from 'classnames';
import { getIn } from 'formik';
import React, {
  Fragment, ReactElement, useCallback, useEffect, useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { SideFilterFieldProps } from './SideFilterFieldProps';
import { tristateEquivalence } from './tristateEquivalence';
import { TristateValues } from './TristateValues';
import Datepicker from 'components/base/Datepicker';
import Input from 'components/base/Input';
import Select from 'components/base/Select';
import SelectButton from 'components/base/SelectButton';

import { DefaultSelectType } from 'models/Others';
import { formatDateForSearch } from 'utils/dateManager';

const getInputValue = (value: unknown): boolean | undefined => {
  if (value === false || value === true) {
    return value;
  }

  return undefined;
};

const SideFilterField = <
  T extends Record<string, unknown>,
  K = Record<string, unknown>
>({
    className,
    clearable,
    filterType,
    formikProps,
    getLabel,
    getValue,
    name,
    options,
    placeholder,
    type,
    disabled,
  }: SideFilterFieldProps<T, K>): ReactElement => {
  const { t } = useTranslation();
  const {
    values, handleBlur, isSubmitting, setFieldValue,
  } = formikProps;

  const classes = classNames('sidefilter-field', className);
  const fieldValue = getIn(values, name);
  const [tristateOptions, settristateOptions] = useState<DefaultSelectType[]>([]);

  useEffect(
    () => {
      settristateOptions([
        { label: t('common.all'), value: TristateValues.all },
        { label: t('common.yes'), value: TristateValues.yes },
        { label: t('common.no'), value: TristateValues.no },
      ]);
    },
    [t],
  );

  const getValueTristate = useCallback(
    (value: boolean | undefined): DefaultSelectType => {
      if (value) {
        return tristateOptions[1];
      }
      if (value === false) {
        return tristateOptions[2];
      }
      return tristateOptions[0];
    },
    [tristateOptions],
  );

  if (filterType === 'input') {
    const isCheckbox = type === 'checkbox';
    return (
      <Input
        key={name}
        checked={isCheckbox ? getInputValue(fieldValue) : undefined}
        className={classes}
        clearable={clearable === undefined ? true : clearable}
        disabled={isSubmitting || disabled}
        name={name}
        placeholder={placeholder}
        type={type || 'text'}
        value={isCheckbox ? undefined : `${fieldValue || ''}`}
        onBlur={handleBlur}
        onChange={(val) => setFieldValue(name, val)}
      />
    );
  }
  if (filterType === 'date') {
    return (
      <Datepicker
        key={name}
        className={classes}
        clearable={clearable === undefined ? true : clearable}
        disabled={isSubmitting || disabled}
        name={name}
        placeholder={placeholder}
        selected={fieldValue ? new Date(fieldValue) : undefined}
        onBlur={handleBlur}
        onChange={(date: Date) => setFieldValue(name, formatDateForSearch(date))}
      />
    );
  }
  if (filterType === 'select' && getLabel && getValue && options) {
    return (
      <Select<K>
        key={name}
        className={classes}
        clearable={clearable === undefined ? true : clearable}
        disabled={isSubmitting || disabled}
        getLabel={getLabel}
        getValue={getValue}
        name={name}
        options={options}
        placeholder={placeholder}
        value={fieldValue as K}
        onBlur={handleBlur}
        onChange={(elem: K) => setFieldValue(name, elem)}
      />
    );
  }
  if (filterType === 'tristate') {
    return (
      <SelectButton<DefaultSelectType>
        key={name}
        className={classes}
        disabled={isSubmitting || disabled}
        getLabel={({ label }: DefaultSelectType) => label}
        getValue={({ value }: DefaultSelectType) => value}
        name={name}
        options={tristateOptions}
        placeholder={placeholder}
        value={getValueTristate(fieldValue as boolean)}
        onChange={(elem: DefaultSelectType) => setFieldValue(
          name,
            tristateEquivalence[elem.value as TristateValues] as boolean,
        )}
      />
    );
  }
  return <Fragment />;
};

export default SideFilterField;
